import { CartUpdateEvent } from '@theme/events';

/**
 * Cart-line quantity control for quantity-pack products.
 *
 * The markup is plain HTML (snippets/dgt-pack-qty.liquid): a "N-Pack x M" label
 * between minus/plus buttons. One delegated click handler steps the line quantity
 * by whole PACKS (each +/- changes it by pack_size units), so a pack's subscription
 * rate always stays on a valid multiple. Dropping below one pack removes the line.
 *
 * We deliberately do NOT send `sections` with the change: that makes Horizon
 * re-render the whole cart section itself (sectionRenderer.renderSection), which
 * rebuilds our label ("x M") correctly from Liquid. Sending our own section HTML
 * triggers a partial morph that preserves our cell and leaves the label stale.
 */

// Build cart endpoints from Shopify.routes.root. NOTE: Theme.routes.cart_change_url
// in this theme is "/cart/change" WITHOUT the ".js" suffix, so POSTing to it returns
// an HTML redirect (not JSON) — the change lands server-side but the follow-up
// .json() throws, so the cart never re-renders until a manual refresh. Always use the
// explicit ".js" endpoints instead.
const CART_ROOT = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const CART_URL = () => `${CART_ROOT()}cart.js`;
const CART_CHANGE_URL = () => `${CART_ROOT()}cart/change.js`;

// Per-line lock (a DOM attribute would be wiped by the section re-render).
const inFlight = new Set();

async function step(cell, isPlus) {
  const lineKey = cell.dataset.lineKey;
  if (!lineKey || inFlight.has(lineKey)) return;
  const size = Math.max(1, parseInt(cell.dataset.packSize || '1', 10));

  inFlight.add(lineKey);
  cell.classList.add('is-busy');
  try {
    // Authoritative current quantity for this line.
    const cart = await (await fetch(CART_URL(), { cache: 'no-store' })).json();
    const line = (cart.items || []).find((i) => i.key === lineKey);
    const current = line ? line.quantity : 0;

    let next = isPlus ? current + size : current - size;
    if (next < size) next = 0; // below one pack -> remove the line

    const component = cell.closest('cart-items-component');
    const sectionId = component ? component.dataset.sectionId : '';

    // Change the line WITHOUT requesting sections.
    const response = await fetch(CART_CHANGE_URL(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: next }),
    });
    const data = await response.json();

    // Dispatch without sections so <cart-items-component> re-renders the section
    // itself (renderSection) and rebuilds the label + price + totals from Liquid.
    cell.dispatchEvent(new CartUpdateEvent(data, sectionId || '', { source: 'dgt-pack-qty' }));
  } catch (error) {
    console.error('[dgt-pack-qty] step failed', error);
  } finally {
    inFlight.delete(lineKey);
    cell.classList.remove('is-busy');
  }
}

// One delegated click handler (modules evaluate once per URL per document).
document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-pq-plus],[data-pq-minus]') : null;
  if (!btn) return;
  const cell = btn.closest('[data-dgt-pack-qty]');
  if (!cell) return;
  event.preventDefault();
  step(cell, btn.hasAttribute('data-pq-plus'));
});
